import React, { Dispatch, MouseEvent, SetStateAction, useCallback, useContext, useEffect, useMemo } from 'react';
import { Organization } from 'sr-types/lib/organization/v1/graphql';
import { Person } from 'sr-types/lib/person/v1/graphql';
import { extractOrgInfoForPreview } from '../../organization/organization';
import { extractPersonBasicInfoForPreview } from '../../person/detailsPage/detailsPageUtilities/personBasicInformation';
import { ValidationRulesType } from '../../production/helpers/productionUtils';
import { UserContext } from '../auth/UserContext';
import {
    DOMAIN_ERROR,
    IMDB_ID_LABEL,
    IMDB_LABEL,
    LINKEDIN_ID_LABEL,
    LINKEDIN_LABEL,
    PROFILE_CONFLICT_ERROR,
    STUDIO_SYSTEM_LABEL,
    UNPARSABLE_ID_ERROR
} from '../constants/alternateIdConstants';
import { FormContext } from '../form/FormContext';
import FormItem from '../form/FormItem';
import FormWidget from '../form/FormWidget';
import I18n from '../i18n/I18n';
import useHistory from '../utils/useHistory';
import AlternateIdInfoAlert from './AlternateIdInfoAlert';
import { fetchResolveAndCreateValidationRules } from './utils/alternateIdFormUtils';

export type AlternateIdValidationInput = {
    profileUrl: string;
    profileId: string;
    entityId: string;
    ownerId: string;
};

type AlternateIdFormProps = {
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
    setDynamicValidationRules: Dispatch<SetStateAction<ValidationRulesType>>;
    getValidationErrors: (input: AlternateIdValidationInput) => Promise<any>;
    hiddenFields?: string[];
    conflictedEntity: Person | Organization;
    setConflictedEntity: Dispatch<SetStateAction<Person | Organization>>;
    fieldSelected: string;
    setFieldSelected: Dispatch<SetStateAction<string>>;
    errorKey: string;
    setErrorKey: Dispatch<SetStateAction<string>>;
    conflictedProfileId?: string;
    setConflictedProfileId?: Dispatch<SetStateAction<string>>;
    isPerson?: boolean;
};

/**
 * Renders a form for managing alternate IDs (IMDb, LinkedIn, Studio System) for a person or organization entity.
 *
 * This component handles user input for alternate ID fields, performs validation checks, and manages
 * conflict resolution when an alternate ID is already associated with another entity. It conditionally
 * displays additional fields and alerts based on the current state and validation results.
 *
 * @param conflictedProfileId - The ID of the profile that has a conflicting alternate ID.
 * @param setConflictedProfileId - Function to update the conflicted profile ID state.
 * @param setIsSaveDisabled - Function to enable or disable the save action based on conflict state.
 * @param setDynamicValidationRules - Function to update dynamic validation rules for the form fields.
 * @param isPerson - Boolean flag indicating if the entity is a person (true) or an organization (false).
 * @param getValidationErrors - Function to fetch and handle validation errors for alternate IDs.
 * @param hiddenFields - An array of field labels to be hidden in the form.
 * @default isPerson false
 * @param conflictedEntity - The entity object that has a conflicting alternate ID.
 * @param setConflictedEntity - Function to update the conflicted entity state.
 * @param fieldSelected - The alternate ID field currently being linked (e.g., IMDb, LinkedIn).
 * @param setFieldSelected - Function to update the selected field state.
 * @param errorKey - Key representing the current error state for alternate ID validation.
 * @param setErrorKey - Function to update the error key state.
 *
 * @returns A React element rendering the alternate ID form with appropriate fields and alerts.
 */
export default function AlternateIdForm({
    setIsSaveDisabled,
    setDynamicValidationRules,
    isPerson = false,
    getValidationErrors,
    hiddenFields,
    conflictedEntity,
    setConflictedEntity,
    fieldSelected,
    setFieldSelected,
    errorKey,
    setErrorKey,
    conflictedProfileId,
    setConflictedProfileId
}: AlternateIdFormProps) {
    const { activeOrganizationAccount, userProfile } = useContext(UserContext);
    const { routeParams } = useHistory();
    const { entityId } = routeParams;
    const { state, setState, validation, handleChange } = useContext(FormContext);
    const id = conflictedProfileId || entityId || userProfile?.personReference?.id;

    useEffect(() => {
        if (errorKey === PROFILE_CONFLICT_ERROR) {
            setIsSaveDisabled(true);
        } else if (!conflictedEntity && validation) {
            setIsSaveDisabled(!validation.isValid);
        }
    }, [errorKey, conflictedEntity, validation, setIsSaveDisabled]);

    const validateUrlLink = useCallback(
        (label: string, value: string, profileId: string) => {
            fetchResolveAndCreateValidationRules(
                label,
                value,
                id,
                activeOrganizationAccount,
                getValidationErrors,
                setDynamicValidationRules,
                setState,
                setConflictedEntity,
                setErrorKey,
                profileId
            );
        },
        [
            id,
            activeOrganizationAccount,
            getValidationErrors,
            setDynamicValidationRules,
            setState,
            setConflictedEntity,
            setErrorKey
        ]
    );

    const {
        onImdbFieldBlur,
        onLinkedInFieldBlur,
        onStudioSystemFieldBlur,
        onImdbProfileIdBlur,
        onLinkedInProfileIdBlur
    } = useMemo(() => {
        const onBlur = (e: MouseEvent<HTMLButtonElement, MouseEvent>, fieldLabel: string, fieldName: string) => {
            const value = e.currentTarget.value;

            setFieldSelected(value !== '' ? fieldLabel : undefined);

            if (state && (fieldName === IMDB_ID_LABEL || fieldName === LINKEDIN_ID_LABEL))
                validateUrlLink(fieldLabel, fieldLabel === IMDB_LABEL ? state.Imdb : state.LinkedIn, value);
            else if (fieldName === IMDB_LABEL || fieldName === LINKEDIN_LABEL || fieldName === STUDIO_SYSTEM_LABEL)
                validateUrlLink(fieldLabel, value, undefined);
        };

        return {
            onImdbFieldBlur: (e: MouseEvent<HTMLButtonElement, MouseEvent>) => onBlur(e, IMDB_LABEL, IMDB_LABEL),
            onLinkedInFieldBlur: (e: MouseEvent<HTMLButtonElement, MouseEvent>) =>
                onBlur(e, LINKEDIN_LABEL, LINKEDIN_LABEL),
            onStudioSystemFieldBlur: (e: MouseEvent<HTMLButtonElement, MouseEvent>) =>
                onBlur(e, STUDIO_SYSTEM_LABEL, STUDIO_SYSTEM_LABEL),
            onImdbProfileIdBlur: (e: MouseEvent<HTMLButtonElement, MouseEvent>) => onBlur(e, IMDB_LABEL, IMDB_ID_LABEL),
            onLinkedInProfileIdBlur: (e: MouseEvent<HTMLButtonElement, MouseEvent>) =>
                onBlur(e, LINKEDIN_LABEL, LINKEDIN_ID_LABEL)
        };
    }, [state, validateUrlLink, setFieldSelected]);

    const clearEntityPreviousStateAfterUpdateClick = useCallback(
        (setConflictedEntity: Dispatch<SetStateAction<Person | Organization>>) => {
            setConflictedEntity(undefined);
            setDynamicValidationRules(undefined);
            setFieldSelected(undefined);
            setIsSaveDisabled(false);
        },
        [setDynamicValidationRules, setFieldSelected, setIsSaveDisabled]
    );

    const setStateToConflictedEntity = useCallback(
        (conflictedEntity: Person | Organization) => {
            const entityUpdate = isPerson
                ? extractPersonBasicInfoForPreview(conflictedEntity as Person)
                : extractOrgInfoForPreview(conflictedEntity as Organization);

            setState({
                entityUpdate
            });
            setConflictedProfileId(conflictedEntity.identity.id);
        },
        [setState, setConflictedProfileId, isPerson]
    );

    const handleChangingStateToConflictedEntity = useCallback(() => {
        if (conflictedEntity) {
            setStateToConflictedEntity(conflictedEntity);
            clearEntityPreviousStateAfterUpdateClick(setConflictedEntity);
        }
    }, [conflictedEntity, setStateToConflictedEntity, clearEntityPreviousStateAfterUpdateClick, setConflictedEntity]);

    const checkIfProfileIdIsShouldBeShown = (field: string) =>
        errorKey === UNPARSABLE_ID_ERROR && (fieldSelected === field || fieldSelected === undefined);

    const showImdbProfileIdField = checkIfProfileIdIsShouldBeShown(IMDB_LABEL) || state?.imdbProfileId;
    const showLinkedInProfileIdField = checkIfProfileIdIsShouldBeShown(LINKEDIN_LABEL) || state?.linkedInProfileId;

    const handleProfileIdChangeAndSetValidation = useCallback(
        (name: string, value: string, fieldLabel: string) => {
            handleChange(name, value);
            if (value && errorKey === UNPARSABLE_ID_ERROR) {
                let validationRule: ValidationRulesType = {};
                validationRule[fieldLabel] = null;
                setDynamicValidationRules((prevState) => ({ ...prevState, ...validationRule }));
            }
        },
        [handleChange, errorKey, setDynamicValidationRules]
    );
    return (
        <>
            <FormItem half hidden={hiddenFields?.includes(IMDB_LABEL)}>
                <FormWidget
                    name="Imdb"
                    label={<I18n token="alternateId.form.IMDb" />}
                    value={state?.Imdb || ''}
                    onBlur={onImdbFieldBlur}
                    disabled={
                        [LINKEDIN_LABEL, STUDIO_SYSTEM_LABEL].includes(fieldSelected) &&
                        errorKey !== DOMAIN_ERROR &&
                        errorKey !== undefined
                    }
                />
            </FormItem>
            <FormItem half hidden={hiddenFields?.includes(LINKEDIN_LABEL)}>
                <FormWidget
                    name="LinkedIn"
                    label={<I18n token="alternateId.form.linkedIn" />}
                    value={state?.LinkedIn || ''}
                    onBlur={onLinkedInFieldBlur}
                    disabled={
                        [IMDB_LABEL, STUDIO_SYSTEM_LABEL].includes(fieldSelected) &&
                        errorKey !== DOMAIN_ERROR &&
                        errorKey !== undefined
                    }
                />
            </FormItem>
            <FormItem half hidden={hiddenFields?.includes(IMDB_LABEL) || !showImdbProfileIdField}>
                <FormWidget
                    name="imdbProfileId"
                    label={<I18n token="alternateId.form.imdb" />}
                    value={state?.imdbProfileId || ''}
                    onChange={(name: string, value: string) =>
                        handleProfileIdChangeAndSetValidation(name, value, IMDB_LABEL)
                    }
                    onBlur={onImdbProfileIdBlur}
                />
            </FormItem>
            <FormItem half hidden={hiddenFields?.includes(LINKEDIN_LABEL) || !showLinkedInProfileIdField}>
                <FormWidget
                    name="linkedInProfileId"
                    value={state?.linkedInProfileId || ''}
                    onChange={(name: string, value: string) =>
                        handleProfileIdChangeAndSetValidation(name, value, LINKEDIN_LABEL)
                    }
                    onBlur={onLinkedInProfileIdBlur}
                    label={<I18n token="alternateId.form.linked.in" />}
                />
            </FormItem>
            <FormItem hidden={hiddenFields?.includes(STUDIO_SYSTEM_LABEL)}>
                <FormWidget
                    name="StudioSystem"
                    label={<I18n token="alternateId.form.studio.system" />}
                    value={state?.StudioSystem || ''}
                    onBlur={onStudioSystemFieldBlur}
                    disabled={
                        [IMDB_LABEL, LINKEDIN_LABEL].includes(fieldSelected) &&
                        errorKey !== DOMAIN_ERROR &&
                        errorKey !== undefined
                    }
                />
            </FormItem>
            <FormItem hidden={(isPerson && !conflictedEntity) || !errorKey}>
                <AlternateIdInfoAlert
                    conflictedEntity={conflictedEntity}
                    fieldSelected={fieldSelected}
                    onUpdate={handleChangingStateToConflictedEntity}
                    isPerson={isPerson}
                    setIsSaveDisabled={setIsSaveDisabled}
                    hasError={Boolean(errorKey)}
                />
            </FormItem>
        </>
    );
}
